import React from 'react';
import { LogoMark } from './Logo';
import { Package, Search } from 'lucide-react';

/**
 * Empty state screens for store pages.
 * variant:
 *   'coming-soon' - store has no products yet (production teaser)
 *   'filter'      - filter/search returned zero results
 *   'orders'      - no orders yet
 */
const EmptyState = ({ variant = 'coming-soon', title, subtitle, action }) => {
  if (variant === 'filter') {
    return (
      <div className="border border-dashed border-[#1f1f1f] rounded-md py-16 flex flex-col items-center justify-center font-mono">
        <Search size={30} className="text-[#3a3a3a] mb-2" />
        <div className="text-[13px] tracking-[0.2em] text-[#8a8a8a]">{title || 'NO RESULTS'}</div>
        <div className="text-[11px] text-[#5a5a5a] mt-1">{subtitle || 'Try a different search or filter.'}</div>
      </div>
    );
  }

  if (variant === 'orders') {
    return (
      <div className="border border-dashed border-[#1f1f1f] rounded-md py-16 flex flex-col items-center justify-center font-mono">
        <Package size={30} className="text-[#3a3a3a] mb-2" />
        <div className="text-[13px] tracking-[0.25em] text-[#8a8a8a]">{title || 'NO ORDERS YET'}</div>
        <div className="text-[11px] text-[#5a5a5a] mt-1">{subtitle || 'Your orders will appear here after purchase.'}</div>
      </div>
    );
  }

  // 'coming-soon' - premium teaser
  return (
    <div className="relative border border-[#151515] bg-[#0a0a0a] rounded-md overflow-hidden font-mono">
      {/* subtle grid background */}
      <div
        className="absolute inset-0 pointer-events-none opacity-[0.04]"
        style={{
          backgroundImage:
            'linear-gradient(#22c55e 1px, transparent 1px), linear-gradient(90deg, #22c55e 1px, transparent 1px)',
          backgroundSize: '28px 28px',
        }}
      />
      {/* glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[380px] h-[380px] rounded-full bg-[#22c55e]/[0.06] blur-3xl pointer-events-none" />

      <div className="relative flex flex-col items-center justify-center py-20 px-6 text-center">
        {/* Big logo mark with pulse */}
        <div className="relative">
          <div className="absolute inset-0 rounded-full bg-[#22c55e]/10 blur-2xl animate-pulse" />
          <div className="relative">
            <LogoMark size={80} />
          </div>
        </div>

        {/* Terminal-style label */}
        <div className="mt-8 flex items-center gap-2 text-[10px] tracking-[0.3em] text-[#22c55e]">
          <span className="w-1.5 h-1.5 rounded-full bg-[#22c55e] animate-pulse" />
          {title || 'STOCKING UP INVENTORY'}
        </div>

        <h2 className="mt-3 text-3xl md:text-4xl text-[#e6e6e6] tracking-wide">
          Available Soon
        </h2>

        <p className="mt-3 max-w-md text-[13px] text-[#8a8a8a] leading-relaxed">
          {subtitle || 'Our premium digital vault is being stocked with curated subscriptions & accounts. Check back shortly for instant-delivery products.'}
        </p>

        {/* Terminal prompt with blinking cursor */}
        <div className="mt-6 inline-flex items-center gap-2 text-[11px] text-[#5a5a5a]">
          <span className="text-[#22c55e]">$</span>
          <span>waiting_for_stock</span>
          <span className="inline-block w-2 h-4 bg-[#22c55e] animate-pulse ml-0.5" />
        </div>

        {/* Feature strip */}
        <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-3 w-full max-w-md">
          {[
            { label: 'INSTANT', sub: 'Auto-delivery' },
            { label: 'SECURE', sub: 'Verified stock' },
            { label: '24/7', sub: 'Live support' },
          ].map((f) => (
            <div key={f.label} className="border border-[#151515] rounded-md px-3 py-2.5 bg-[#080808]">
              <div className="text-[10px] tracking-[0.3em] text-[#22c55e]">{f.label}</div>
              <div className="text-[10px] text-[#7a7a7a] mt-0.5">{f.sub}</div>
            </div>
          ))}
        </div>

        {action && <div className="mt-6">{action}</div>}
      </div>
    </div>
  );
};

export default EmptyState;
